import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import API from '../utils/api';
import { FaEnvelope, FaArrowLeft, FaLock, FaPaperPlane, FaShieldAlt, FaMobileAlt, FaArrowRight, FaCheckCircle } from 'react-icons/fa';
import './ForgotPassword.css';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Please enter your email address or mobile number');
      return;
    }

    setLoading(true);

    try {
      const response = await API.post('/auth/forgot-password', { email });
      
      setEmailSent(true);
      toast.success('Password reset instructions sent!');
      
      // In development, show the reset link in console
      if (response.data.resetLink) {
        console.log('🔗 Password Reset Link:', response.data.resetLink);
        console.log('User:', response.data.debug?.name, '(' + response.data.debug?.email + ')');
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.message || 'Failed to send reset email');
      setLoading(false);
    }
  };

  return (
    <div className="font-sans fp-page-wrapper">
      <div className="fp-app-container">
        
        {/* ─── LEFT PANEL (Teal Graphic) ─── */}
        <div className="fp-left-panel">
          {/* Background Elements */}
          <div className="fp-orb-1"></div>
          <div className="fp-orb-2"></div>

          <h2 className="fp-title">Forgot<br/>Password?</h2>
          <p className="fp-subtitle">
            No worries! We'll help you<br/>reset your password.
          </p>

          {/* Central Padlock Illustration */}
          <div className="fp-illustration">
            <div className="fp-concentric-circles">
              <div className="fp-circle-1"></div>
              <div className="fp-circle-2"></div>
              <div className="fp-circle-3"></div>
              <FaLock className="fp-padlock" />
              <FaPaperPlane className="fp-paper-plane" />
            </div>
          </div>

          {/* Bottom Security Badge */}
          <div className="fp-security-badge">
            <div className="fp-shield-icon">
              <FaShieldAlt />
            </div>
            <div>
              <div className="fp-badge-title">Your security is our priority</div>
              <div className="fp-badge-desc">We'll never share your information.</div>
            </div>
          </div>
        </div>

        {/* ─── RIGHT PANEL (Form Area) ─── */}
        <div className="fp-right-panel">
          
          <Link to="/login" className="fp-back-link">
            <FaArrowLeft /> Back to Sign in
          </Link>

          {!emailSent ? (
            /* ─── STEP 1: FORM ─── */
            <div className="flex-1 flex flex-col justify-center">
              <div className="fp-form-header">
                <div className="fp-envelope-icon-box">
                  <FaEnvelope />
                </div>
                <h3 className="fp-form-title">Forgot Password?</h3>
                <p className="fp-form-desc">
                  Enter your registered email ID or mobile number and we'll send you a link to reset your password.
                </p>
              </div>

              {/* Progress Stepper */}
              <div className="fp-stepper">
                <div className="fp-stepper-line"></div>
                <div className="fp-step active">
                  <div className="fp-step-circle">1</div>
                  <div className="fp-step-label">Enter Details</div>
                </div>
                <div className="fp-step">
                  <div className="fp-step-circle">2</div>
                  <div className="fp-step-label">Verify</div>
                </div>
                <div className="fp-step">
                  <div className="fp-step-circle">3</div>
                  <div className="fp-step-label">Reset Password</div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="fp-form">
                <div className="fp-input-group">
                  <FaEnvelope className="fp-input-icon" />
                  <input
                    type="text"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="fp-input"
                    placeholder="Enter your email ID or mobile number"
                    required
                    disabled={loading}
                  />
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="fp-btn-primary"
                >
                  {loading ? 'Sending...' : 'Send Reset Link'} 
                  {!loading && <FaArrowRight />}
                </button>

                <div className="fp-divider">OR</div>

                <button
                  type="button"
                  className="fp-btn-secondary"
                  onClick={() => document.querySelector('.fp-input').focus()}
                >
                  <FaMobileAlt /> Reset via Mobile Number
                </button>
              </form>
            </div>
          ) : (
            /* ─── STEP 2: SUCCESS ─── */
            <div className="flex-1 flex flex-col justify-center fp-success-state">
              <div className="fp-success-icon">
                <FaCheckCircle />
              </div>
              <h3 className="fp-form-title">Check Your Email</h3>
              <p className="fp-form-desc" style={{ marginBottom: '24px' }}>
                We've sent password reset instructions to <strong>{email}</strong>
              </p>
              <p className="text-sm text-gray-500 mb-8">
                Didn't receive the email? Check your spam folder or try again.
              </p>
              
              <div className="flex flex-col gap-3" style={{ maxWidth: '300px', margin: '0 auto' }}>
                <button
                  onClick={() => {
                    setEmailSent(false);
                    setEmail('');
                  }}
                  className="fp-btn-secondary"
                >
                  Try Another Email
                </button>
                <Link to="/login" className="fp-btn-primary" style={{ textDecoration: 'none' }}>
                  Back to Login
                </Link>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
